/**
 * A catalogue item's attachment rule — Step 73's last piece.
 *
 * Two flags, one question: may this request carry files, and must it. The
 * reference offers exactly this pair on every item, and the pair is the point
 * — "allowed" and "required" are different decisions and a single tri-state
 * dropdown ("off / optional / required") reads worse in the editor and is
 * harder to widen later.
 *
 * ── Why this is shared code rather than an `if` in the handler ─────────
 *
 * The same rule has to hold in three places: the agent form, the portal form,
 * and the submit endpoints behind both. A check written at the submit door
 * only is a form that lets somebody fill in ten fields and then refuses; a
 * check written in the browser only is not a check. So the rule lives here,
 * the two forms call it to decide whether to block their own submit button,
 * and both servers call it as the actual gate.
 *
 * ── Refusing is the server's job even when the form is honest ──────────
 *
 * `requireAttachment` without `allowAttachments` is not expressible through
 * the editor, but it IS expressible through the API — and it would be an item
 * nobody can ever submit. `attachmentError` treats it as "not required",
 * because a contradictory configuration must not become a deadlock nobody can
 * diagnose from the outside. The editor prevents the state; this makes it
 * harmless if it arrives anyway.
 */

export interface AttachmentPolicy {
  /** Offer the file control on this item's form at all. */
  allowAttachments: boolean;
  /** ...and refuse a submission without at least one. */
  requireAttachment: boolean;
}

/**
 * Null when the submission may proceed, else the sentence to show.
 *
 * Deliberately returns the message rather than a boolean: the copy is the
 * same on both surfaces, and two call sites writing their own wording is how
 * a requester and an agent end up being told different things about the same
 * refusal.
 */
export function attachmentError(policy: AttachmentPolicy, count: number): string | null {
  if (!policy.allowAttachments) return null;
  if (!policy.requireAttachment) return null;
  return count > 0 ? null : 'This request needs at least one file attached.';
}

/**
 * Files sent for an item that does not accept any.
 *
 * Separate from `attachmentError` because it is a different failure with a
 * different audience: the first is a person who forgot something, this is a
 * client sending what no form offered. Dropped rather than refused — the
 * request itself is valid and rejecting it would strand a submission over
 * files nobody asked for.
 */
export function attachmentsToKeep(policy: AttachmentPolicy, ids: string[]): string[] {
  return policy.allowAttachments ? ids : [];
}
